import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import YogaCard from "./YogaCard";
import FilterPanel from "./FilterPanel";
import Pagination from "./Pagination";
import { fetchFilterOptions, searchRetreats } from "../services/api";
import { getPrefetchedListing } from "../lib/prefetchListing";
import { groupTeacherOfferingsForListing } from "../lib/groupTeacherListings";
import { ONLINE_COURSE, PRENATAL_YOGA } from "../lib/courseTypes";
import { ArrowUpDown, Loader2, ChevronDown, Star } from "lucide-react";

const defaultFilters = {
  nameQuery: "",
  locationQuery: "",
  type: "",
  style: "",
  meals: "",
  source: "",
  duration: "",
  rating: "",
  maxPrice: "",
  sort: "latest",
  startDate: "",
  endDate: "",
};

/**
 * Shared retreat listing UI used by Home, location pages and the teacher-training page.
 * Pass `lockedLocation` / `lockedType` to force (and hide) a filter for a dedicated page.
 */
function RetreatListing({
  lockedLocation = "",
  lockedType = "",
  excludedTypes = [],
  heading = "Yoga Retreats, Meditation Retreats & Teacher Training Retreats",
  subheading = "Compare retreats in India by destination, style, duration, rating and price",
  pageSize = 12,
}) {
  const { pathname } = useLocation();
  const prefetched = getPrefetchedListing(pathname);
  const shouldGroupTeachers =
    lockedType === ONLINE_COURSE || lockedType === PRENATAL_YOGA;
  const hidePrices = lockedType === ONLINE_COURSE;
  // Online courses listing is instructor-only (no school providers like Yogkulam).
  const listingGroupOpts = {
    hidePrices,
    teachersOnly: lockedType === ONLINE_COURSE,
  };

  const [yogaCenters, setYogaCenters] = useState(() => {
    const items = prefetched?.items ?? [];
    if (!shouldGroupTeachers) return items;
    // Prefetch may only hold the first API page; still group+slice so the
    // first paint matches the client-paginated card model used after fetch.
    const grouped = groupTeacherOfferingsForListing(items, listingGroupOpts);
    return grouped.slice(0, pageSize);
  });
  const [total, setTotal] = useState(() => {
    if (!shouldGroupTeachers) return prefetched?.total ?? 0;
    const items = prefetched?.items ?? [];
    return groupTeacherOfferingsForListing(items, listingGroupOpts).length;
  });
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ types: [], styles: [], meals: [], sources: [], durations: [], locations: [] });
  const [loading, setLoading] = useState(() => !prefetched);
  const [error, setError] = useState("");
  const requestSeqRef = useRef(0);
  const listingTopRef = useRef(null);
  const skipInitialFetchRef = useRef(Boolean(prefetched));

  const [filters, setFilters] = useState(defaultFilters);

  const lockedFields = [
    ...(lockedLocation ? ["location"] : []),
    ...(lockedType ? ["type"] : []),
  ];

  const excludedTypesKey = excludedTypes.join(",");
  const filteredOptions = {
    ...options,
    types: (options.types || []).filter((t) => !excludedTypes.includes(t)),
  };

  useEffect(() => {
    fetchFilterOptions(lockedType).then((data) => setOptions(data));
  }, [lockedType]);

  // Reset to first page whenever a filter or the locked context changes.
  useEffect(() => {
    setPage(1);
  }, [
    filters.nameQuery,
    filters.locationQuery,
    filters.type,
    filters.style,
    filters.meals,
    filters.source,
    filters.duration,
    filters.rating,
    filters.maxPrice,
    filters.sort,
    filters.startDate,
    filters.endDate,
    lockedLocation,
    lockedType,
    excludedTypesKey,
  ]);

  useEffect(() => {
    const skipPrefetchOnly =
      skipInitialFetchRef.current &&
      lockedType !== ONLINE_COURSE &&
      lockedType !== PRENATAL_YOGA;
    if (skipPrefetchOnly) {
      skipInitialFetchRef.current = false;
      return;
    }
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
    }

    setLoading(true);
    setError("");

    const requestSeq = ++requestSeqRef.current;

    const timer = setTimeout(() => {
      // Online/Prenatal cards are one-per-instructor/school after grouping.
      // Fetch the full merged set first, group, then paginate cards — otherwise
      // instructors whose offerings fall past the first API page disappear and
      // Pagination hides page 2 because total was fudged from a partial page.
      searchRetreats({
        nameQuery: filters.nameQuery,
        locationQuery: lockedLocation || filters.locationQuery,
        type: lockedType || filters.type,
        excludeTypes: excludedTypes,
        style: filters.style,
        meals: filters.meals,
        source: filters.source,
        duration: filters.duration,
        rating: filters.rating,
        maxPrice: hidePrices ? "" : filters.maxPrice,
        sort: hidePrices && (filters.sort === "price_low" || filters.sort === "price_high")
          ? "latest"
          : filters.sort,
        startDate: filters.startDate,
        endDate: filters.endDate,
        limit: shouldGroupTeachers ? 200 : pageSize,
        offset: shouldGroupTeachers ? 0 : (page - 1) * pageSize,
      })
        .then(({ total: t, items }) => {
          if (requestSeq !== requestSeqRef.current) return;
          if (shouldGroupTeachers) {
            const grouped = groupTeacherOfferingsForListing(items || [], listingGroupOpts);
            const start = (page - 1) * pageSize;
            setYogaCenters(grouped.slice(start, start + pageSize));
            setTotal(grouped.length);
          } else {
            setYogaCenters(items);
            setTotal(t);
          }
        })
        .catch((e) => {
          if (requestSeq !== requestSeqRef.current) return;
          console.error(e);
          setError("Failed to fetch retreats. Please try again later.");
        })
        .finally(() => {
          if (requestSeq !== requestSeqRef.current) return;
          setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [
    filters.nameQuery,
    filters.locationQuery,
    filters.type,
    filters.style,
    filters.meals,
    filters.source,
    filters.duration,
    filters.rating,
    filters.maxPrice,
    filters.sort,
    filters.startDate,
    filters.endDate,
    lockedLocation,
    lockedType,
    excludedTypesKey,
    page,
    pageSize,
    shouldGroupTeachers,
    hidePrices,
  ]);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    if (listingTopRef.current) {
      listingTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Clear price sort/budget if user landed with them while prices are hidden.
  useEffect(() => {
    if (!hidePrices) return;
    setFilters((prev) => {
      if (prev.maxPrice === "" && prev.sort !== "price_low" && prev.sort !== "price_high") {
        return prev;
      }
      return {
        ...prev,
        maxPrice: "",
        sort: prev.sort === "price_low" || prev.sort === "price_high" ? "latest" : prev.sort,
      };
    });
  }, [hidePrices]);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Sidebar */}
      <aside className="w-full lg:w-80 shrink-0">
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          options={filteredOptions}
          lockedFields={lockedFields}
          hideBudget={hidePrices}
        />
      </aside>

      {/* Main Content */}
      <main className="flex-grow" id="retreat-listings" ref={listingTopRef}>
        <div className="bg-white rounded-3xl p-6 mb-8 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {heading}
            </h2>
            <p className="text-sm text-gray-500 font-medium">{subheading}</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Rating Filter */}
            <div className="relative">
              <select
                value={filters.rating || ""}
                onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                className="appearance-none pl-10 pr-10 py-2.5 bg-gray-50 border-none rounded-2xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
              >
                <option value="">Rating: Any</option>
                <option value="4.5+">4.5+ &amp; Up</option>
                <option value="4.0+">4.0+ &amp; Up</option>
                <option value="3.5+">3.5+ &amp; Up</option>
                <option value="3.0+">3.0+ &amp; Up</option>
              </select>
              <Star className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-500 fill-yellow-500 pointer-events-none" size={16} />
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                className="appearance-none pl-10 pr-10 py-2.5 bg-gray-50 border-none rounded-2xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
              >
                <option value="">Sort by: Latest</option>
                <option value="rating-high">Rating: High to Low</option>
                <option value="rating-low">Rating: Low to High</option>
                {!hidePrices && (
                  <>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </>
                )}
                <option value="newest">Most Recent</option>
              </select>
              <ArrowUpDown size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-2xl mb-8 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">!</div>
            <p className="font-medium text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Loader2 className="animate-spin mb-4" size={40} />
            <p className="font-bold text-sm uppercase tracking-widest">Finding your perfect retreat...</p>
          </div>
        ) : (
          <>
            {yogaCenters.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                  {yogaCenters.map((yoga, index) => (
                    <div key={yoga.id || yoga.retreat_key || index} className="h-full min-h-0">
                      <YogaCard
                        yoga={yoga}
                        hidePrices={hidePrices}
                      />
                    </div>
                  ))}
                </div>

                <Pagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="bg-white rounded-3xl p-20 text-center border-2 border-dashed border-gray-100">
                <Search size={48} className="mx-auto text-gray-200 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No retreats found</h3>
                <p className="text-gray-500 max-w-xs mx-auto text-sm">
                  We couldn't find any retreats matching your current filters. Try adjusting your search criteria.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default RetreatListing;
